import React, {useEffect, useState} from 'react';
import {
    Box,
    IconButton,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow, Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {InputField, MoneyField} from '@/components/common';

interface AllowanceScheduleTableProps {
    currency: string | null;
    /** value1 from parent */
    valuePriorYear: number;
    /** value2 from parent */
    valueThisYear: number;
    /** input value from parent */
    reducingBf: number;
    bf?: number;
    bfChange?: (bf: number) => void;
    nextYear?: boolean;
    leasehold?: boolean;
    /** backend-computed annual allowance (used directly when nextYear=true) */
    backendAnnualAllowance?: number;
    /** backend-computed total allowance (used directly when nextYear=true) */
    backendTotalAllowance?: number;
    /** backend-computed addition during the year (used directly when nextYear=true) */
    backendAddDuringTheYear?: number;
    /** backend-computed subtotal after annual allowance (before sales proceed) */
    backendSubtotalAfterAllowance?: number;
    /** RX12 disposal: sales proceed (negative, HK$/2) */
    backendSalesProceed?: number;
    backendBalancingCharge?: number;
    backendSubtotalAfterSales?: number;
    backendReducingValueCf?: number;
    /** when true and effectiveBf===0, show the "prior years fully used" switch */
    showPriorYearFullyUsed?: boolean;
    /** controlled value for the switch */
    priorYearFullyUsed?: boolean;
    /** callback when switch toggles */
    priorYearFullyUsedChange?: (v: boolean) => void;
    /** persisted commercial prior-years label override */
    commercialPriorLabel?: string | null;
    /** persisted commercial prior-years value override (HK$/2) */
    commercialPriorValue?: number | null;
    /**
     * Persist overrides: both non-null to save; both null to clear.
     * Called only when both have values, or when cancel clears both to null.
     */
    onCommercialPriorOverrideChange?: (label: string | null, value: number | null) => void;
}

export function AllowanceScheduleTable({
                                           currency,
                                           valuePriorYear,
                                           valueThisYear,
                                           reducingBf,
                                           bf: bfProp,
                                           bfChange,
                                           nextYear = false,
                                           leasehold = false,
                                           backendAnnualAllowance,
                                           backendTotalAllowance,
                                           backendAddDuringTheYear,
                                           backendSubtotalAfterAllowance,
                                           backendSalesProceed,
                                           backendBalancingCharge,
                                           backendSubtotalAfterSales,
                                           backendReducingValueCf,
                                           showPriorYearFullyUsed = false,
                                           priorYearFullyUsed,
                                           priorYearFullyUsedChange,
                                           commercialPriorLabel,
                                           commercialPriorValue,
                                           onCommercialPriorOverrideChange,
                                       }: AllowanceScheduleTableProps) {
    const asPositive = (n: number) => {
        const v = Number(n) || 0;
        return v < 0 ? -v : v;
    };

    const [bf, setBf] = useState<number>(() => asPositive(Number(bfProp ?? reducingBf) || 0));
    useEffect(() => {
        setBf(asPositive(Number(bfProp ?? reducingBf) || 0));
    }, [bfProp, reducingBf]);

    const calculatedPriorYearValue = leasehold ? (Number(valuePriorYear) || 0) : (Number(valuePriorYear) || 0) * 0.5;
    const thisYearValue = leasehold ? (Number(valueThisYear) || 0) : (Number(valueThisYear) || 0) * 0.5;

    // Commercial (!nextYear && !leasehold) or leasehold (nextYear && leasehold)
    const allowPriorOverride = Boolean(leasehold) === Boolean(nextYear);
    const hasPersistedOverride = allowPriorOverride
        && commercialPriorLabel != null
        && String(commercialPriorLabel).trim() !== ''
        && commercialPriorValue != null
        && Number.isFinite(Number(commercialPriorValue));

    const [priorEditMode, setPriorEditMode] = useState<boolean>(() => hasPersistedOverride);
    const [draftPriorLabel, setDraftPriorLabel] = useState<string>(() =>
        hasPersistedOverride ? String(commercialPriorLabel) : '',
    );
    /** Seeded from calculatedPriorYearValue on Edit; user edits trigger save */
    const [draftPriorValue, setDraftPriorValue] = useState<number | null>(() =>
        hasPersistedOverride ? Number(commercialPriorValue) : null,
    );
    /** True after user interacts with the prior-value MoneyField (blur/change), incl. same numeric value */
    const priorValueUserEditedRef = React.useRef<boolean>(false);

    useEffect(() => {
        if (!allowPriorOverride) {
            setPriorEditMode(false);
            setDraftPriorLabel('');
            setDraftPriorValue(null);
            priorValueUserEditedRef.current = false;
            return;
        }
        if (hasPersistedOverride) {
            setPriorEditMode(true);
            setDraftPriorLabel(String(commercialPriorLabel));
            setDraftPriorValue(Number(commercialPriorValue));
            priorValueUserEditedRef.current = false;
        } else if (!priorEditMode) {
            setDraftPriorLabel('');
            setDraftPriorValue(null);
            priorValueUserEditedRef.current = false;
        }
        // Only re-hydrate from persisted props; ignore local edit drafting
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowPriorOverride, hasPersistedOverride, commercialPriorLabel, commercialPriorValue]);

    const priorYearValue = priorEditMode && draftPriorValue != null && Number.isFinite(draftPriorValue)
        ? draftPriorValue
        : calculatedPriorYearValue;
    const sumValue = priorYearValue + thisYearValue; // step 3.2.3

    const effectiveBf = asPositive(bfProp !== undefined ? Number(bfProp) || 0 : bf);
    const showSwitch = showPriorYearFullyUsed && effectiveBf === 0;
    const switchValue = priorYearFullyUsed ?? false;
    const fullyUsed = switchValue;
    const showFullyUsedMessage = showSwitch && switchValue;
    const showAllowanceDetail = !fullyUsed || !showSwitch;

    const addition = fullyUsed ? 0 : thisYearValue; // step 3.2.5
    const sumBfAdd = effectiveBf + addition;
    const localCalculatedAllowance = fullyUsed
        ? 0
        : Math.round(sumValue * 0.04);
    // Edit (prior override): keep min(4%, bf+addition). Non-edit commercial: no bf cap.
    const localTotalAllowance = fullyUsed
        ? 0
        : Math.min(localCalculatedAllowance, sumBfAdd);

    const salesProceed = Number(backendSalesProceed) || 0;
    const hasDisposal = Math.abs(salesProceed) > 0;

    // When prior override edit mode is active, prefer local formula over backend snapshot
    const useLocalAllowance = priorEditMode && draftPriorValue != null;
    // Prior-row schedules (commercial / leasehold) non-edit: ROUND(costSum*4%) only — no bf cap
    const useUncappedCommercialAllowance = allowPriorOverride && !useLocalAllowance;

    const aaTotalAllowance = useLocalAllowance
        ? localTotalAllowance
        : (useUncappedCommercialAllowance
            ? localCalculatedAllowance
            : (backendTotalAllowance != null
                ? Math.abs(Number(backendTotalAllowance))
                : localTotalAllowance));
    const annualAllowance = useLocalAllowance || useUncappedCommercialAllowance
        ? -aaTotalAllowance
        : (backendAnnualAllowance != null
            ? Number(backendAnnualAllowance)
            : -aaTotalAllowance);

    const subtotalAfterAllowance = useLocalAllowance || useUncappedCommercialAllowance
        ? sumBfAdd - aaTotalAllowance
        : (backendSubtotalAfterAllowance != null
            ? Number(backendSubtotalAfterAllowance)
            : sumBfAdd - aaTotalAllowance);

    const subtotalAfterSales = useLocalAllowance || useUncappedCommercialAllowance
        ? subtotalAfterAllowance + salesProceed
        : (backendSubtotalAfterSales != null
            ? Number(backendSubtotalAfterSales)
            : subtotalAfterAllowance + salesProceed);

    const balancingCharge = useLocalAllowance || useUncappedCommercialAllowance
        ? (subtotalAfterSales < 0 ? Math.abs(subtotalAfterSales) : 0)
        : (backendBalancingCharge != null
            ? Number(backendBalancingCharge)
            : (subtotalAfterSales < 0 ? Math.abs(subtotalAfterSales) : 0));

    let totalAllowanceValue: number;
    let cf: number;

    if (fullyUsed) {
        totalAllowanceValue = 0;
        cf = 0;
    } else if (hasDisposal && balancingCharge > 0) {
        totalAllowanceValue = aaTotalAllowance;
        cf = 0;
    } else if (hasDisposal) {
        totalAllowanceValue = useLocalAllowance || useUncappedCommercialAllowance
            ? aaTotalAllowance
            : (backendTotalAllowance != null
                ? Number(backendTotalAllowance)
                : aaTotalAllowance);
        cf = Math.max(0, useLocalAllowance || useUncappedCommercialAllowance
            ? subtotalAfterSales
            : (backendReducingValueCf != null
                ? Number(backendReducingValueCf)
                : subtotalAfterSales));
    } else {
        totalAllowanceValue = useLocalAllowance || useUncappedCommercialAllowance
            ? aaTotalAllowance
            : (backendTotalAllowance != null
                ? Number(backendTotalAllowance)
                : aaTotalAllowance);
        cf = Math.max(0, useLocalAllowance || useUncappedCommercialAllowance
            ? sumBfAdd - aaTotalAllowance
            : (backendReducingValueCf != null
                ? Number(backendReducingValueCf)
                : sumBfAdd - aaTotalAllowance));
    }

    const salesProceedRaw = Math.abs(salesProceed) * 2;

    const handleBfChange = (n: number) => {
        const next = asPositive(n);
        bfChange?.(next);
        if (bfProp === undefined) {
            setBf(next);
        }
    };

    const handleSwitchChange = (checked: boolean) => {
        priorYearFullyUsedChange?.(checked);
    };

    const labelPrior = nextYear ? 'Leasehold Improvement in prior years' : 'Cost of properties in prior years';

    const tryPersistPriorOverride = (label: string, value: number | null, opts?: {fromValueEdit?: boolean}) => {
        if (opts?.fromValueEdit) {
            priorValueUserEditedRef.current = true;
        }
        // Seed-only Edit must not save; wait until user edits the value field (or already persisted + label edit)
        if (!priorValueUserEditedRef.current && !hasPersistedOverride) {
            return;
        }
        const trimmed = (label ?? '').trim();
        if (!trimmed || value == null || !Number.isFinite(value)) {
            return;
        }
        onCommercialPriorOverrideChange?.(trimmed, value);
    };

    const handlePriorEditStart = () => {
        const seedLabel = hasPersistedOverride
            ? String(commercialPriorLabel)
            : labelPrior;
        // Seed draft from calculated value only — do not auto-save until user edits
        priorValueUserEditedRef.current = false;
        setDraftPriorLabel(seedLabel);
        setDraftPriorValue(calculatedPriorYearValue);
        setPriorEditMode(true);
    };

    const handlePriorEditCancel = () => {
        setPriorEditMode(false);
        setDraftPriorLabel('');
        setDraftPriorValue(null);
        priorValueUserEditedRef.current = false;
        if (hasPersistedOverride) {
            onCommercialPriorOverrideChange?.(null, null);
        }
    };

    const priorTitleValue = Number(valuePriorYear).toLocaleString('en-US') || 0;
    const thisYearTitleValue = Number(valueThisYear).toLocaleString('en-US') || 0;
    const labelThisYear = nextYear ? 'Leasehold Improvement in this year' : 'Cost of properties in this years';
    const labelBf = nextYear ? 'W.D.V b/f' : 'Reducing value b/f';
    const labelAddition = nextYear ? 'Addition during the year' : 'Addition during the year';
    const labelCf = nextYear ? 'W.D.V c/f' : 'Reducing value c/f';

    const showPriorRow = calculatedPriorYearValue !== 0 || priorEditMode || hasPersistedOverride;

    return (
        <Box>
            <Table size="small" sx={{tableLayout: 'fixed', width: '100%'}}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{width: '58%'}}/>
                        <TableCell align="center" sx={{width: '27%'}}/>
                        <TableCell align="center" sx={{width: '15%'}}>Total Allowance</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* 3.2.1 */}
                    {showPriorRow && (
                        <TableRow>
                            <TableCell sx={{overflow: 'hidden'}}>
                                {priorEditMode && allowPriorOverride ? (
                                    <InputField
                                        size="small"
                                        value={draftPriorLabel}
                                        onChange={(e) => {
                                            const next = e.target.value ?? '';
                                            setDraftPriorLabel(next);
                                        }}
                                        onBlur={() => tryPersistPriorOverride(draftPriorLabel, draftPriorValue)}
                                        sx={{width: '100%', maxWidth: '100%'}}
                                    />
                                ) : nextYear ? (
                                    <Typography variant="body2">{labelPrior}</Typography>
                                ) : (
                                    <Typography variant="body2">{labelPrior} (HK$ {priorTitleValue}/2)</Typography>
                                )}
                            </TableCell>
                            <TableCell align="center" sx={{py: 0, overflow: 'hidden'}}>
                                {priorEditMode && allowPriorOverride ? (
                                    <Box sx={{width: '52.5%', mx: 'auto'}}>
                                        <MoneyField
                                            tiny
                                            value={draftPriorValue ?? calculatedPriorYearValue}
                                            editable
                                            currencySymbol={currency || ''}
                                            showCents={false}
                                            allowNegative={false}
                                            integerOnly
                                            align="center"
                                            onChangeNumber={(n) => {
                                                const next = Math.max(0, Math.trunc(Number(n) || 0));
                                                setDraftPriorValue(next);
                                                tryPersistPriorOverride(draftPriorLabel, next, {fromValueEdit: true});
                                            }}
                                            sx={{width: '100%', minWidth: 0}}
                                        />
                                    </Box>
                                ) : (
                                    <MoneyField value={priorYearValue} editable={false} currencySymbol={currency || ''}
                                                showCents={false}
                                                align="center"/>
                                )}
                            </TableCell>
                            <TableCell align="center" sx={{width: '15%', overflow: 'hidden'}}>
                                {allowPriorOverride && (
                                    <IconButton
                                        size="small"
                                        aria-label={priorEditMode ? 'cancel-prior-override' : 'edit-prior-override'}
                                        onClick={priorEditMode ? handlePriorEditCancel : handlePriorEditStart}
                                    >
                                        {priorEditMode
                                            ? <CancelOutlinedIcon fontSize="small"/>
                                            : <EditOutlinedIcon fontSize="small"/>}
                                    </IconButton>
                                )}
                            </TableCell>
                        </TableRow>
                    )}
                    {/* 3.2.2 */}
                    {thisYearValue !== 0 && (
                        <TableRow>
                            <TableCell>
                                {nextYear ? (
                                    <Typography variant="body2">{labelThisYear}</Typography>
                                ) : (
                                    <Typography
                                        variant="body2">{labelThisYear} (HK$ {thisYearTitleValue}/2)</Typography>
                                )}
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField value={thisYearValue} editable={false} currencySymbol={currency || ''}
                                            showCents={false}
                                            align="center"/>
                            </TableCell>
                            <TableCell/>
                        </TableRow>
                    )}
                    {/* 3.2.3 sum of above */}
                    <TableRow>
                        <TableCell/>
                        <TableCell align="center">
                            <MoneyField value={sumValue} editable={false} currencySymbol={currency || ''}
                                        showCents={false}
                                        align="center"/>
                        </TableCell>
                        <TableCell/>
                    </TableRow>
                    {/* 3.2.4 Reducing value b/f */}
                    <TableRow>
                        <TableCell>
                            <Typography variant="body2">{labelBf}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{py: 0}}>
                            <Box sx={{width: '35%', mx: 'auto'}}>
                                <MoneyField
                                    tiny
                                    value={effectiveBf}
                                    editable
                                    currencySymbol={currency || ''}
                                    showCents={false}
                                    align="center"
                                    onChangeNumber={handleBfChange}
                                    sx={{width: '100%', minWidth: 0}}
                                />
                            </Box>
                        </TableCell>
                        <TableCell/>
                    </TableRow>
                    {showSwitch && (
                        <TableRow>
                            <TableCell align="right" colSpan={2}>
                                <Typography variant="body2">
                                    Reducing value used up in prior years?
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Switch
                                    checked={switchValue}
                                    onChange={(e) => handleSwitchChange(e.target.checked)}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                    {showFullyUsedMessage ? (
                        <TableRow>
                            <TableCell colSpan={3}>
                                <Typography variant="body2">
                                    Tax allowance were fully used in prior years.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {showAllowanceDetail && (
                        <>
                            {/* 3.2.5 Addition during the year (HK$/2) */}
                            {addition !== 0 && (
                                <TableRow>
                                    <TableCell>
                                        {nextYear ? (
                                            <Typography variant="body2">{labelAddition}</Typography>
                                        ) : (
                                            <Typography
                                                variant="body2">{labelAddition} (HK$ {thisYearTitleValue}/2)</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <MoneyField value={addition} editable={false} currencySymbol={currency || ''}
                                                    showCents={false}
                                                    align="center"/>
                                    </TableCell>
                                    <TableCell/>
                                </TableRow>
                            )}
                            {/* 3.2.6 sum of b/f and addition — only when addition row is shown */}
                            {addition !== 0 && (
                                <TableRow>
                                    <TableCell/>
                                    <TableCell align="center">
                                        <MoneyField value={sumBfAdd} editable={false}
                                                    currencySymbol={currency || ''}
                                                    showCents={false}
                                                    align="center"/>
                                    </TableCell>
                                    <TableCell/>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell>
                                    <Typography variant="body2">Less: Annual allowance 4%</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField value={annualAllowance} editable={false} currencySymbol={currency || ''}
                                                showCents={false} align="center"/>
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField value={totalAllowanceValue} editable={false}
                                                currencySymbol={currency || ''}
                                                showCents={false} align="center"/>
                                </TableCell>
                            </TableRow>
                            {hasDisposal && (
                                <>
                                    <TableRow>
                                        <TableCell/>
                                        <TableCell align="center">
                                            <MoneyField value={subtotalAfterAllowance} editable={false}
                                                        currencySymbol={currency || ''}
                                                        showCents={false} align="center"/>
                                        </TableCell>
                                        <TableCell/>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {nextYear ? (
                                                    'Less: sales proceed'
                                                ) : (
                                                    <>Less: sales proceed (HK$ {salesProceedRaw.toLocaleString('en-US')}/2)</>
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <MoneyField value={salesProceed} editable={false}
                                                        currencySymbol={currency || ''}
                                                        showCents={false} align="center"/>
                                        </TableCell>
                                        <TableCell/>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell/>
                                        <TableCell align="center">
                                            <MoneyField value={subtotalAfterSales} editable={false}
                                                        currencySymbol={currency || ''}
                                                        showCents={false} align="center"/>
                                        </TableCell>
                                        <TableCell/>
                                    </TableRow>
                                </>
                            )}
                            {hasDisposal && balancingCharge > 0 && (
                                <TableRow>
                                    <TableCell>
                                        <Typography variant="body2">Less: Balancing charge</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <MoneyField value={balancingCharge} editable={false}
                                                    currencySymbol={currency || ''}
                                                    showCents={false} align="center"/>
                                    </TableCell>
                                    <TableCell align="center">
                                        <MoneyField value={-balancingCharge} editable={false}
                                                    currencySymbol={currency || ''}
                                                    showCents={false} align="center"/>
                                    </TableCell>
                                </TableRow>
                            )}
                            {/* 3.2.8 Reducing value c/f */}
                            <TableRow>
                                <TableCell>
                                    <Typography variant="body2">{labelCf}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField value={cf} editable={false} currencySymbol={currency || ''}
                                                showCents={false}
                                                align="center"/>
                                </TableCell>
                                <TableCell/>
                            </TableRow>
                        </>
                    )}

                    <TableRow>
                        <TableCell colSpan={3} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={3} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    )
        ;
}
